import React from "react";
import { Link, useParams } from "react-router-dom";
import { Button, Divider, Menu, MenuItem } from "@mui/material";
import { EditOutlined, MoreVert } from "@mui/icons-material";
import { toast } from "react-toastify";
import { useAuth } from "hooks";
import {
  useApproveLocalTransportMutation,
  useGetLocalTransportDetailQuery,
} from "store/services/localTransportApi";
import BreadcrumbLayout from "layouts/BreadcrumbLayout";
import LocalTransportBasicDetail from "./LocalTransportComponents/LocalTransportBasicDetail";
import {
  LocalTransportDetailContext,
  useLocalTransportDetailContext,
} from "contexts/LocalTransportDetailContext";
import LocalTransportDetailTab from "./LocalTransportComponents/LocalTransportDetailTab";

const LocalTransportDetailScreen = () => {
  const { id } = useParams();

  const {
    data: localTransportDetail,
    isLoading: loadingLocalTransport,
    error,
  } = useGetLocalTransportDetailQuery(id);

  const breadcrumbs = [
    { title: "Local Transports", path: "/app/crm/local-transport/list" },
    { title: "Detail" },
  ];

  return (
    <LocalTransportDetailContext.Provider
      value={{ localTransportDetail, loadingLocalTransport }}
    >
      <BreadcrumbLayout
        breadcrumbs={breadcrumbs}
        isBusy={loadingLocalTransport}
        headerActions={<PageActions />}
      >
        <BreadcrumbLayout.Error error={error} />
        <BreadcrumbLayout.Paper>
          <LocalTransportBasicDetail />
          <Divider />
          <LocalTransportDetailTab />
        </BreadcrumbLayout.Paper>
      </BreadcrumbLayout>
    </LocalTransportDetailContext.Provider>
  );
};

export default LocalTransportDetailScreen;

const PageActions = () => {
  const { localTransportDetail } = useLocalTransportDetailContext();
  const { isSystemAdmin, isAccountAdmin, loggedInUser } =
    useAuth();
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [approveLocalTransport, { isLoading }] =
    useApproveLocalTransportMutation();

  const handleMenuOpen = (event) => setAnchorEl(event.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  const handleApproval = async (status) => {
    try {
      await approveLocalTransport({
        _id: localTransportDetail._id,
        status,
      }).unwrap();
      toast.success(
        status === "Manual Approved"
          ? "Local transport request approved successfully."
          : "Local transport request rejected."
      );
      handleMenuClose();
    } catch (err) {
      toast.error(err?.data?.message || "Failed to update approval status.");
    }
  };

  const canApproveOrReject =
    isSystemAdmin ||
    isAccountAdmin ||
    // isEntityAdmin ||
    localTransportDetail?.approver?._id === loggedInUser?._id;

  if (!canApproveOrReject) return null;

  return (
    <div className="flex items-center gap-4">
      {localTransportDetail?.status === "Pending" && (
        <>
          <Link to={`/app/crm/local-transport/${localTransportDetail?._id}/edit`}>
            <Button startIcon={<EditOutlined />} variant="contained">
              Edit
            </Button>
          </Link>
          <>
            {isAccountAdmin && (<Button
              variant="outlined"
              endIcon={<MoreVert />}
              onClick={handleMenuOpen}
              disabled={isLoading}
            >
              Actions
            </Button>)}
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
            >
              <MenuItem onClick={() => handleApproval("Manual Approved")}>
                Approve
              </MenuItem>
              <MenuItem onClick={() => handleApproval("Rejected")}>
                Reject
              </MenuItem>
            </Menu>
          </></>
      )}
    </div>
  );
};
